import { api } from "services/restUtil";
import { apiQuery, infiniteApiQuery, PaginatedResponse } from "utils/queries";
import { LessonSession, LessonSessionStatus, MonitoringLessonSession } from "./model";

export const upcomingSessionsQuery = apiQuery<PaginatedResponse<LessonSession>>({
  key: "/sessions/lesson/upcoming",
});

type FetchCompletedSessionsParams = {
  page: number;
  size: number;
};

export function fetchCompletedSessions(args: FetchCompletedSessionsParams) {
  return api.get<PaginatedResponse<LessonSession>>("/sessions/lesson/completed", args);
}

/**
 * Most recent completed lesson per student, for the signed-in instructor.
 *
 * The endpoint has no per-student aggregate, so this reduces one page of the
 * instructor's completed sessions (newest first). That page is expensive — full
 * session objects, hundreds of KB — so it is cached hard and never refetched on
 * window focus; enable it only where a recent lesson changes what is rendered.
 *
 * A student last taught more than RECENT_LESSONS_PAGE_SIZE sessions ago reads as
 * "no recent lesson", which is the right answer for every question this feeds.
 */
export const RECENT_LESSONS_PAGE_SIZE = 100;

export const lastLessonPerStudentQuery = apiQuery<Record<string, string>>({
  key: "/sessions/lesson/completed#last-per-student",
  queryFn: async () => {
    const response = await fetchCompletedSessions({
      page: 1,
      size: RECENT_LESSONS_PAGE_SIZE,
    });
    return response.list.reduce<Record<string, string>>((latest, session) => {
      const current = latest[session.user.id];
      if (!current || current < session.scheduled) {
        latest[session.user.id] = session.scheduled;
      }
      return latest;
    }, {});
  },
  // The roster's dormancy grouping does not change minute to minute, and this is the
  // heaviest request the sidebar makes. One fetch per working session is enough.
  staleTime: 30 * 60 * 1000,
  refetchOnWindowFocus: false,
});

type CompletedSessionsQueryParams = {
  team_id?: string;
  member_id?: string;
  page?: number;
  size?: number;
};

export const completedSessionsQuery = infiniteApiQuery<
  LessonSession,
  CompletedSessionsQueryParams | void,
  void
>({
  path: "/sessions/lesson/completed",
  queryFn: (path, queryParams) => api.get(path, queryParams),
});

type FetchSessionArgs = {
  id: string;
};

export function fetchSession(args: FetchSessionArgs) {
  return api.get<LessonSession>(`/session/lesson/${args.id}`);
}

type FetchSessionsForMonitoringArgs = {
  from: string;
  to: string;
};

type FetchSessionsForMonitoringResponse = {
  list: MonitoringLessonSession[];
};

export function fetchSessionsForMonitoring(args: FetchSessionsForMonitoringArgs) {
  return api
    .get<FetchSessionsForMonitoringResponse>("/sessions/lesson/monitoring", args)
    .then(resp => resp.list);
}

type SearchSessionsParams = {
  search?: string;
  statuses?: LessonSessionStatus[];
  page?: number;
  size?: number;
  order?: "asc" | "desc";
  from?: string;
  to?: string;
};

export type SearchSessionsResponse = {
  list: LessonSession[];
  total_count: number;
};

export function searchSessions({ search, statuses, ...args }: SearchSessionsParams) {
  return api.get<SearchSessionsResponse>("/sessions/lesson", {
    ...args,
    contains: search || undefined,
    filter: statuses?.length ? `status;${statuses.join(",")}` : undefined,
  });
}

type SearchSessionsQueryParams = {
  contains?: string;
  statuses?: LessonSessionStatus[];
  order?: "asc" | "desc";
};

export const searchLessonSessionsQuery = infiniteApiQuery<
  LessonSession,
  SearchSessionsQueryParams | void
>({
  path: "/sessions/lesson",
  queryFn: (_path, queryParams) =>
    searchSessions({ search: queryParams?.contains, ...queryParams }),
});
